/**
 * Shipping Insurance Core
 *
 * Shared storefront logic for:
 * - Selecting Standard, Enhanced, or Premium insurance from merchandise subtotal
 * - Excluding every insurance tier from its own subtotal calculation
 * - Keeping Standard insurance aligned to the shortest subscription cadence
 * - Keeping Enhanced and Premium insurance one-time only
 * - Swapping stale, duplicate, or legacy insurance lines
 *
 * Existing offer code uses the SubscriptionInsuranceCore name, so that public API
 * remains stable while the implementation now owns all insurance-tier decisions.
 */

window.SubscriptionInsuranceCore = (function() {
  'use strict';

  var DEFAULT_TIERS = [
    {
      key: 'standard',
      variantId: '51706610188583',
      minSubtotal: 0,
      maxSubtotal: 50000,
      price: 995,
      supportsSubscription: true
    },
    {
      key: 'enhanced',
      variantId: '51706610221351',
      minSubtotal: 50000,
      maxSubtotal: 100000,
      price: 2495,
      supportsSubscription: false
    },
    {
      key: 'premium',
      variantId: '51706610254119',
      minSubtotal: 100000,
      maxSubtotal: null,
      price: 4995,
      supportsSubscription: false
    }
  ];
  var DEFAULT_LEGACY_VARIANT_IDS = ['50757737873703'];

  var cartInsuranceState = { hasInsurance: false };
  var initialized = false;
  var cachedCartItems = null;
  var lastResolvedTier = null;

  function normalizeId(value) {
    if (value === null || value === undefined) return '';
    var text = String(value).trim();
    if (!text) return '';
    var matches = text.match(/\d+/g);
    return matches && matches.length ? matches[matches.length - 1] : text;
  }

  function toCents(value) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? Math.round(parsed) : 0;
  }

  function normalizeTier(tier, fallback) {
    var source = tier || {};
    var base = fallback || {};
    var configuredMaximum =
      source.maxSubtotal === undefined ? base.maxSubtotal : source.maxSubtotal;
    return {
      key: source.key || base.key,
      variantId: normalizeId(source.variantId || base.variantId),
      minSubtotal: toCents(
        source.minSubtotal === undefined ? base.minSubtotal : source.minSubtotal
      ),
      maxSubtotal:
        configuredMaximum === null
          ? null
          : toCents(configuredMaximum),
      price: toCents(source.price === undefined ? base.price : source.price),
      supportsSubscription:
        source.supportsSubscription === undefined
          ? Boolean(base.supportsSubscription)
          : Boolean(source.supportsSubscription)
    };
  }

  function getInsuranceConfig() {
    var settings = window.theme && window.theme.shopSettings
      ? window.theme.shopSettings
      : {};
    var configuredTiers = Array.isArray(settings.shippingInsuranceTiers)
      ? settings.shippingInsuranceTiers
      : [];
    var tiers = DEFAULT_TIERS.map(function(defaultTier, index) {
      return normalizeTier(configuredTiers[index], defaultTier);
    }).filter(function(tier) {
      return Boolean(tier.variantId);
    });

    var legacyVariantIds = (
      Array.isArray(settings.shippingInsuranceLegacyVariantIds)
        ? settings.shippingInsuranceLegacyVariantIds
        : DEFAULT_LEGACY_VARIANT_IDS
    ).map(normalizeId).filter(Boolean);
    var variantIds = tiers.map(function(tier) {
      return tier.variantId;
    }).concat(legacyVariantIds).filter(function(id, index, all) {
      return id && all.indexOf(id) === index;
    });

    return {
      enabled: Boolean(settings.shippingInsuranceEnabled),
      variantId: tiers[0] ? tiers[0].variantId : '',
      variantIds: variantIds,
      legacyVariantIds: legacyVariantIds,
      tiers: tiers,
      sellingPlans: settings.insuranceSellingPlans || {}
    };
  }

  function isInsuranceVariantId(value, config) {
    var insuranceConfig = config || getInsuranceConfig();
    var normalized = normalizeId(value);
    return Boolean(normalized && insuranceConfig.variantIds.indexOf(normalized) !== -1);
  }

  function getLineVariantId(line) {
    return normalizeId(
      line && (
        line.variant_id ||
        line.variantId ||
        line.merchandise_id ||
        (line.merchandise && line.merchandise.id) ||
        line.id
      )
    );
  }

  function getLineFinalPrice(line) {
    if (!line) return 0;
    if (Number.isFinite(Number(line.final_line_price))) {
      return toCents(line.final_line_price);
    }
    if (Number.isFinite(Number(line.line_price))) {
      return toCents(line.line_price);
    }
    if (line.cost && line.cost.totalAmount) {
      return Math.round(Number(line.cost.totalAmount.amount || 0) * 100);
    }
    return toCents(line.price) * Math.max(1, toCents(line.quantity));
  }

  function getMerchandiseSubtotal(items, config) {
    var insuranceConfig = config || getInsuranceConfig();
    return (items || []).reduce(function(total, line) {
      if (isInsuranceVariantId(getLineVariantId(line), insuranceConfig)) return total;
      return total + getLineFinalPrice(line);
    }, 0);
  }

  function resolveTierForSubtotal(subtotal, config) {
    var insuranceConfig = config || getInsuranceConfig();
    var cents = Math.max(0, toCents(subtotal));
    var tier = insuranceConfig.tiers.find(function(candidate) {
      var meetsMinimum = cents >= candidate.minSubtotal;
      var belowMaximum =
        candidate.maxSubtotal === null || cents < candidate.maxSubtotal;
      return meetsMinimum && belowMaximum;
    });
    return tier || insuranceConfig.tiers[insuranceConfig.tiers.length - 1] || null;
  }

  // Owner, Oct 18: a 9mm Club order keeps Standard recurring insurance, the one its later boxes carry, instead of moving
  // up to one-time Enhanced when the first order crosses $500. Premium ($1,000+) is untouched and stays opt-in.
  function isClubLine(line) {
    var properties = (line && line.properties) || {};
    if (properties._aov_offer === '9mm-club') return true;
    return Array.isArray(line && line.attributes) && line.attributes.some(function(attribute) {
      return attribute && attribute.key === '_aov_offer' && attribute.value === '9mm-club';
    });
  }

  function holdClubTier(tier, items, config, clubOrder) {
    if (!tier || tier.supportsSubscription || isPremiumTier(tier)) return tier;
    if (!clubOrder && !(items || []).some(isClubLine)) return tier;
    var insuranceConfig = config || getInsuranceConfig();
    return insuranceConfig.tiers.find(function(candidate) {
      return candidate.supportsSubscription;
    }) || tier;
  }

  function getLineSellingPlan(line) {
    return line && (
      line.selling_plan_allocation ||
      (line.merchandise && line.merchandise.sellingPlan)
    );
  }

  function parseFrequencyFromText(value) {
    var text = String(value || '');
    var match = text.match(/(\d{1,4})/);
    return match ? parseInt(match[1], 10) : null;
  }

  function getLineFrequency(line, config) {
    if (!line) return null;
    var properties = line.properties || {};
    var attributes = line.attributes || [];
    var propertyFrequency =
      properties._subscription_frequency ||
      properties.subscription_frequency;
    if (!propertyFrequency && Array.isArray(attributes)) {
      var frequencyAttribute = attributes.find(function(attribute) {
        return attribute && (
          attribute.key === '_subscription_frequency' ||
          attribute.key === 'subscription_frequency'
        );
      });
      propertyFrequency = frequencyAttribute && frequencyAttribute.value;
    }
    var fromProperty = parseFrequencyFromText(propertyFrequency);
    if (fromProperty) return fromProperty;

    var allocation = line.selling_plan_allocation;
    var planName = allocation && allocation.selling_plan
      ? allocation.selling_plan.name
      : '';
    var fromName = parseFrequencyFromText(planName);
    if (fromName) return fromName;

    var planId = normalizeId(
      allocation && allocation.selling_plan
        ? allocation.selling_plan.id
        : allocation && allocation.selling_plan_id
    );
    if (!planId) return null;

    var sellingPlans = (config || getInsuranceConfig()).sellingPlans || {};
    var entries = Object.entries(sellingPlans);
    for (var index = 0; index < entries.length; index += 1) {
      if (normalizeId(entries[index][1]) === planId) {
        return parseInt(entries[index][0], 10) || null;
      }
    }
    return null;
  }

  function getShortestFrequency(items, config, newFrequency) {
    var insuranceConfig = config || getInsuranceConfig();
    var frequencies = (items || []).filter(function(line) {
      return (
        !isInsuranceVariantId(getLineVariantId(line), insuranceConfig) &&
        Boolean(getLineSellingPlan(line))
      );
    }).map(function(line) {
      return getLineFrequency(line, insuranceConfig);
    }).filter(function(frequency) {
      return Number.isFinite(frequency) && frequency > 0;
    });

    var parsedNewFrequency = Number(newFrequency);
    if (Number.isFinite(parsedNewFrequency) && parsedNewFrequency > 0) {
      frequencies.push(parsedNewFrequency);
    }
    return frequencies.length ? Math.min.apply(Math, frequencies) : null;
  }

  function inspectCartItems(items, options) {
    var config = getInsuranceConfig();
    var cartItems = items || [];
    var settings = options || {};
    var merchandiseSubtotal =
      settings.merchandiseSubtotal === undefined
        ? getMerchandiseSubtotal(cartItems, config)
        : toCents(settings.merchandiseSubtotal);
    merchandiseSubtotal = Math.max(
      0,
      merchandiseSubtotal + toCents(settings.merchandiseSubtotalDelta)
    );
    var tier = holdClubTier(resolveTierForSubtotal(merchandiseSubtotal, config), cartItems, config, settings.clubOrder);
    var insuranceItems = cartItems.filter(function(line) {
      return isInsuranceVariantId(getLineVariantId(line), config);
    });
    var matchingInsuranceItem = tier
      ? insuranceItems.find(function(line) {
          return getLineVariantId(line) === tier.variantId;
        })
      : null;
    var insuranceItem = matchingInsuranceItem || insuranceItems[0] || null;
    var hasSubscriptionProducts = cartItems.some(function(line) {
      return (
        !isInsuranceVariantId(getLineVariantId(line), config) &&
        Boolean(getLineSellingPlan(line))
      );
    });
    var insuranceType = insuranceItem
      ? (getLineSellingPlan(insuranceItem) ? 'subscription' : 'onetime')
      : null;

    return {
      hasInsurance: insuranceItems.length > 0,
      insuranceItems: insuranceItems,
      insuranceLineKeys: insuranceItems.map(function(line) {
        return line && (line.key || line.id);
      }).filter(Boolean),
      insuranceLineKey: insuranceItem && (insuranceItem.key || insuranceItem.id) || null,
      insuranceVariantId: insuranceItem ? getLineVariantId(insuranceItem) : null,
      insuranceType: insuranceType,
      insuranceFrequency:
        insuranceType === 'subscription'
          ? getLineFrequency(insuranceItem, config)
          : null,
      hasSubscriptionProducts: hasSubscriptionProducts,
      merchandiseSubtotal: merchandiseSubtotal,
      expectedTier: tier
    };
  }

  async function fetchCartItems() {
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    var response = await fetch(root + 'cart.js');
    if (!response.ok) throw new Error('Failed to fetch cart');
    var cart = await response.json();
    cachedCartItems = cart.items || [];
    return cachedCartItems;
  }

  async function checkCartForInsurance(options) {
    var config = getInsuranceConfig();
    if (!config.variantId) {
      cartInsuranceState = { hasInsurance: false };
      initialized = true;
      return cartInsuranceState;
    }

    try {
      var items = await fetchCartItems();
      cartInsuranceState = inspectCartItems(items, options);
    } catch (error) {
      console.warn('[InsuranceCore] Could not check cart:', error);
      cartInsuranceState = { hasInsurance: false };
    }

    initialized = true;
    return cartInsuranceState;
  }

  async function getShortestTenorInCart(newFrequency) {
    var config = getInsuranceConfig();
    var items = cachedCartItems;
    if (!items) {
      try {
        items = await fetchCartItems();
      } catch (error) {
        console.warn('[InsuranceCore] Could not fetch cart:', error);
        return Number(newFrequency) || null;
      }
    }
    return getShortestFrequency(items, config, newFrequency);
  }

  function buildAction(action, state, targetType, frequency) {
    var tier = state.expectedTier;
    lastResolvedTier = tier;
    return {
      action: action,
      addType: targetType,
      frequency: frequency || null,
      variantId: tier && tier.variantId,
      tier: tier && tier.key,
      removeKey: state.insuranceLineKey,
      removeKeys: state.insuranceLineKeys || []
    };
  }

  function isPremiumTier(tier) {
    return Boolean(tier) && (tier.key === 'premium' || Number(tier.minSubtotal || 0) >= 100000);
  }

  async function determineInsuranceAction(isSubscription, newProductFrequency, options) {
    var config = getInsuranceConfig();
    var state = await checkCartForInsurance(options);
    if (!config.enabled || !state.expectedTier) return { action: 'none' };

    var targetTier = state.expectedTier;
    lastResolvedTier = targetTier;
    // Owner decision Oct 13: the premium tier ($1,000+ carts) is opt-in. Never auto-add it or upgrade into it; a lower
    // tier that no longer fits is removed. A premium line the shopper added stays (normal checks below).
    if (isPremiumTier(targetTier)) {
      var hasOnlyPremium = state.hasInsurance && (state.insuranceItems || []).length === 1 &&
        state.insuranceVariantId === targetTier.variantId;
      if (!hasOnlyPremium) {
        if (state.hasInsurance) return { action: 'remove', removeKeys: state.insuranceLineKeys || [], tier: 'premium-opt-in' };
        return { action: 'none', tier: 'premium-opt-in' };
      }
    }
    // options.oneTimeOnly: callers whose subscription has a fixed end (the 6-charge Fighting Words series) keep the
    // protection one-time, so it can't turn into an open-ended monthly charge. Every other caller is unchanged.
    var shouldSubscribe =
      !(options && options.oneTimeOnly) &&
      targetTier.supportsSubscription &&
      (Boolean(isSubscription) || state.hasSubscriptionProducts);
    var targetType = shouldSubscribe ? 'subscription' : 'onetime';
    var targetFrequency = shouldSubscribe
      ? await getShortestTenorInCart(newProductFrequency)
      : null;
    var hasWrongCount = (state.insuranceItems || []).length !== 1;
    var hasWrongVariant =
      state.hasInsurance && state.insuranceVariantId !== targetTier.variantId;
    var hasWrongType =
      state.hasInsurance && state.insuranceType !== targetType;
    var hasWrongFrequency =
      state.hasInsurance &&
      targetType === 'subscription' &&
      targetFrequency &&
      state.insuranceFrequency !== targetFrequency;

    if (!state.hasInsurance) {
      return buildAction('add', state, targetType, targetFrequency);
    }
    if (hasWrongCount || hasWrongVariant || hasWrongType || hasWrongFrequency) {
      return buildAction('swap', state, targetType, targetFrequency);
    }
    return { action: 'none', variantId: targetTier.variantId, tier: targetTier.key };
  }

  async function removeInsuranceFromCart(lineKey) {
    if (!lineKey) return;
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    try {
      var response = await fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });
      if (!response.ok) throw new Error('Failed to remove insurance from cart');
      cachedCartItems = null;
    } catch (error) {
      console.warn('[InsuranceCore] Error removing insurance:', error);
    }
  }

  // Remove every insurance line except one matching `keepItem` ({ id, selling_plan }, as passed to cart/add.js), one
  // line at a time from a fresh cart read. With a discount code in the cart (FREEDOM5 from the popup), Shopify re-keys
  // lines after every add or change, so keys read earlier fail with 400 'no valid id' and both lines reach checkout
  // (order #PA52681, Oct 19). keepItem = null removes them all (pages that remove the old line before adding the new).
  // Never throws; resolves { ok, removed }, ok false when an extra insurance line may still be in the cart.
  async function removeReplacedInsurance(keepItem) {
    var config = getInsuranceConfig();
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    var fetcher = window.__nativeFetch || window.fetch;
    var keepVariant = keepItem ? normalizeId(keepItem.id) : null;
    var keepPlan = keepItem ? normalizeId(keepItem.selling_plan) : '';
    var removed = 0;
    var linePlanId = function(line) {
      var allocation = line && line.selling_plan_allocation;
      return normalizeId(allocation && allocation.selling_plan && allocation.selling_plan.id);
    };

    try {
      for (var attempt = 0; attempt < 6; attempt += 1) {
        var response = await fetcher(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) break;
        var cart = await response.json();
        var keeper = null;
        var extra = (cart.items || []).find(function(line) {
          if (!isInsuranceVariantId(getLineVariantId(line), config)) return false;
          var isKeeper = keepVariant !== null &&
            getLineVariantId(line) === keepVariant &&
            linePlanId(line) === keepPlan;
          if (isKeeper && !keeper) {
            keeper = line;
            return false;
          }
          return true;
        });
        cachedCartItems = null;
        // Shopify merges an add into an identical line, so the kept line can end up at quantity 2: bring it back to 1.
        var fix = extra
          ? { key: extra.key, quantity: 0 }
          : keeper && Number(keeper.quantity) > 1
            ? { key: keeper.key, quantity: 1 }
            : null;
        if (!fix) return { ok: true, removed: removed };

        var change = await fetcher(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: fix.key, quantity: fix.quantity })
        });
        if (change.ok) {
          if (!fix.quantity) removed += 1;
        } else {
          console.warn('[InsuranceCore] Could not remove replaced insurance line:', change.status);
        }
      }
    } catch (error) {
      console.warn('[InsuranceCore] Error removing replaced insurance:', error);
    }
    return { ok: false, removed: removed };
  }

  function getInsuranceSellingPlanId(frequency) {
    var config = getInsuranceConfig();
    if (!frequency || !config.sellingPlans) return null;
    return config.sellingPlans[frequency] || config.sellingPlans[String(frequency)] || null;
  }

  function getTierByVariantId(variantId, config) {
    var insuranceConfig = config || getInsuranceConfig();
    var normalized = normalizeId(variantId);
    return insuranceConfig.tiers.find(function(tier) {
      return tier.variantId === normalized;
    }) || null;
  }

  function buildInsuranceItem(type, frequency, variantId) {
    var config = getInsuranceConfig();
    var tier =
      getTierByVariantId(variantId, config) ||
      lastResolvedTier ||
      config.tiers[0];
    if (!tier || !tier.variantId) {
      console.warn('[InsuranceCore] No insurance variant ID configured');
      return null;
    }

    var item = {
      id: parseInt(tier.variantId, 10),
      quantity: 1
    };

    if (type === 'subscription' && tier.supportsSubscription) {
      var sellingPlanId = getInsuranceSellingPlanId(frequency);
      if (!sellingPlanId && config.sellingPlans) {
        var fallbackFrequencies = [30, 60, 90, 120];
        for (var index = 0; index < fallbackFrequencies.length; index += 1) {
          sellingPlanId = config.sellingPlans[fallbackFrequencies[index]];
          if (sellingPlanId) break;
        }
      }
      if (sellingPlanId) {
        var parsedPlan = parseInt(sellingPlanId, 10);
        item.selling_plan = Number.isNaN(parsedPlan) ? sellingPlanId : parsedPlan;
      } else {
        console.warn('[InsuranceCore] No Standard insurance selling plan found');
      }
    }

    return item;
  }

  async function executeInsuranceAction(action) {
    if (!action || action.action === 'none') return null;
    var removeKeys = action.removeKeys && action.removeKeys.length
      ? action.removeKeys
      : action.removeKey
        ? [action.removeKey]
        : [];

    // Remove by current keys, not removeKeys: each removal re-keys the other lines when a discount code is in the cart.
    if (action.action === 'remove' || action.action === 'swap') {
      if (removeKeys.length) await removeReplacedInsurance(null);
      if (action.action === 'remove') return null;
    }
    if (action.action === 'add' || action.action === 'swap') {
      return buildInsuranceItem(action.addType, action.frequency, action.variantId);
    }
    return null;
  }

  function getState() {
    return Object.assign({}, cartInsuranceState);
  }

  function isInitialized() {
    return initialized;
  }

  function clearCartCache() {
    cachedCartItems = null;
  }

  return {
    getInsuranceConfig: getInsuranceConfig,
    normalizeId: normalizeId,
    isInsuranceVariantId: isInsuranceVariantId,
    getMerchandiseSubtotal: getMerchandiseSubtotal,
    resolveTierForSubtotal: resolveTierForSubtotal,
    holdClubTier: holdClubTier,
    inspectCartItems: inspectCartItems,
    checkCartForInsurance: checkCartForInsurance,
    getShortestTenorInCart: getShortestTenorInCart,
    determineInsuranceAction: determineInsuranceAction,
    isPremiumTier: isPremiumTier,
    removeInsuranceFromCart: removeInsuranceFromCart,
    removeReplacedInsurance: removeReplacedInsurance,
    getInsuranceSellingPlanId: getInsuranceSellingPlanId,
    buildInsuranceItem: buildInsuranceItem,
    executeInsuranceAction: executeInsuranceAction,
    getState: getState,
    isInitialized: isInitialized,
    clearCartCache: clearCartCache
  };
})();
